import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DoctorOnboarding } from '../../../core/models/doctor-onboarding.model';
import { DoctorProfileService } from '../../../core/services/doctor-profile.service';
import { Doctor } from '../../../core/models/doctor.model';
import { catchError, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '../../../core/services/notification.service';

// Helper: display text → Java-style UPPER_SNAKE_CASE enum key
const toEnumKey = (label: string) => {
  return label.toUpperCase().replace(/ & /g, '_AND_').replace(/ /g, '_');
};

@Component({
  selector: 'doctor-onboarding',
  imports: [CommonModule, ReactiveFormsModule],
  providers: [FormBuilder],
  templateUrl: './doctor-onboarding-component.html',
  styleUrl: './doctor-onboarding-component.scss',
})
export class DoctorOnboardingComponent implements OnInit {
  @Output() submitDoctor = new EventEmitter<DoctorOnboarding>();
  form!: FormGroup;
  isLoading = false;

  /** When true the doctor wants to create their own clinic on sign-up. */
  createClinic = false;

  titles = ['DR', 'PROF', 'MR', 'MRS', 'MISS'];

  private specialisationsList = [
    'Cardiology',
    'Dermatology',
    'Endocrinology',
    'Gastroenterology',
    'Neurology',
    'Obstetrics & Gynecology',
    'Oncology',
    'Ophthalmology',
    'Orthopedics',
    'Pediatrics',
    'Psychiatry',
    'Radiology',
    'Urology',
    'General Surgery',
    'Internal Medicine',
    'Family Medicine',
    'Anesthesiology',
    'Emergency Medicine',
    'Infectious Diseases',
    'Rheumatology',
  ];

  specializationOptions = this.specialisationsList.map((label) => ({
    label,
    value: toEnumKey(label),
  }));

  constructor(
    public fb: FormBuilder,
    private router: Router,
    private notificationService: NotificationService,
    private profileService: DoctorProfileService,
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      // Personal Details (always required)
      title: ['', [Validators.required, Validators.maxLength(20)]],
      gender: ['', [Validators.required, Validators.maxLength(10)]],
      experience: [null, [Validators.required, Validators.min(0), Validators.max(80)]],
      licenseNumber: ['', [Validators.required, Validators.maxLength(20)]],
      specialization: ['', [Validators.required, Validators.maxLength(100)]],

      // Practice Details (required only when createClinic = true)
      practiceNumber: ['', [Validators.maxLength(20)]],
      learningInstitution: ['', [Validators.maxLength(150)]],
      highestQualification: ['', [Validators.maxLength(150)]],
      qualificationCertificateNumber: ['', [Validators.maxLength(50)]],
      practiceName: ['', [Validators.maxLength(150)]],

      // Address Details (required only when createClinic = true)
      addressLine1: ['', [Validators.maxLength(150)]],
      addressLine2: ['', [Validators.maxLength(150)]],
      addressLine3: ['', [Validators.maxLength(150)]],
      city: ['', [Validators.maxLength(80)]],
      postalCode: ['', [Validators.pattern(/^[0-9]{4,10}$/)]],
    });
  }

  /** Toggle the "Create Clinic" checkbox and update validators accordingly. */
  onCreateClinicChange(checked: boolean): void {
    this.createClinic = checked;

    const practiceFields = [
      'practiceNumber',
      'learningInstitution',
      'highestQualification',
      'qualificationCertificateNumber',
      'practiceName',
    ];
    const addressFields = ['addressLine1', 'city', 'postalCode'];

    if (checked) {
      ['practiceNumber', 'practiceName'].forEach((field) =>
        this.form.get(field)?.setValidators([Validators.required, Validators.maxLength(20)]),
      );
      ['learningInstitution', 'highestQualification'].forEach((field) =>
        this.form.get(field)?.setValidators([Validators.required, Validators.maxLength(150)]),
      );
      ['addressLine1', 'city'].forEach((field) =>
        this.form.get(field)?.setValidators([Validators.required, Validators.maxLength(150)]),
      );
      this.form
        .get('postalCode')
        ?.setValidators([Validators.required, Validators.pattern(/^[0-9]{4,10}$/)]);
    } else {
      [...practiceFields, ...addressFields].forEach((field) => {
        this.form.get(field)?.clearValidators();
        this.form.get(field)?.setValue('');
      });
    }

    [...practiceFields, ...addressFields].forEach((field) =>
      this.form.get(field)?.updateValueAndValidity(),
    );
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const formValue = this.form.value;

    const addressParts = [
      formValue.addressLine1,
      formValue.addressLine2,
      formValue.addressLine3,
      formValue.city,
      formValue.postalCode,
    ].filter((part: string) => part && part.trim());

    const clinicAddress = this.createClinic ? addressParts.join(', ') : '';

    const doctorPayload: Doctor = {
      practiceNumber: this.createClinic ? formValue.practiceNumber : undefined,
      licenseNumber: formValue.licenseNumber,
      specialization: formValue.specialization,
      experienceYears: formValue.experience,
      clinicAddress: clinicAddress,
      learningInstitution: this.createClinic ? formValue.learningInstitution : undefined,
      qualification: this.createClinic ? formValue.highestQualification : undefined,
      qualificationCertificateNumber: this.createClinic
        ? formValue.qualificationCertificateNumber
        : undefined,
      practiceName: this.createClinic ? formValue.practiceName : undefined,
      createClinic: this.createClinic,

      user: {
        title: formValue.title,
        gender: formValue.gender,
        addressLine1: this.createClinic ? formValue.addressLine1 : undefined,
        addressLine2: this.createClinic ? formValue.addressLine2 || undefined : undefined,
        addressLine3: this.createClinic ? formValue.addressLine3 || undefined : undefined,
        city: this.createClinic ? formValue.city : undefined,
        postalCode: this.createClinic ? formValue.postalCode : undefined,
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
      } as any,

      id: '',
      userId: '',
      bio: undefined,
      openingTime: undefined,
      closingTime: undefined,
      slotDuration: undefined,
      dateCreated: undefined,
      preBookingNotes: '',
      profileStatus: undefined,
      onlineStatus: undefined,
      face2faceConsultationEnabled: true,
      onlineConsultationEnabled: false,
      suspensionReason: undefined,
    } as Doctor;

    this.profileService
      .createDoctorProfile(doctorPayload)
      .pipe(
        tap((response) => {
          this.submitDoctor.emit(formValue as DoctorOnboarding);
          this.isLoading = false;
          this.router.navigate(['dashboard/doctor-dashboard'], { state: { doctorData: response } });
        }),
        catchError((error) => {
          this.isLoading = false;
          this.notificationService.error(error?.error?.message || 'Failed to submit profile.');
          return throwError(() => error);
        }),
      )
      .subscribe();
  }
}
